import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, RefreshCw, AlertCircle, CheckCircle2,
  Lock, Unlock, ShieldAlert, ArrowUpRight, CalendarClock, X, Search,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Safely coerce anything an API might put in `error.response.data.detail`
// into a display string. FastAPI validation errors return an array of
// {loc, msg, type, ...} objects; rendering those directly as a React child
// crashes the page. Fall back to a supplied default message.
const errorToString = (detail, fallback = 'Something went wrong') => {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parts = detail.map((d) => (d && d.msg) ? d.msg : (typeof d === 'string' ? d : ''));
    const joined = parts.filter(Boolean).join(', ');
    return joined || fallback;
  }
  if (typeof detail === 'object' && detail.msg) return String(detail.msg);
  return fallback;
};

// Local-date DOB parser — matches the pattern in AdminPlayerReportPage.js and
// AdminRegistrationsList.js so all admin surfaces agree on the calendar.
const parseLocalDate = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const fmtDOB = (s) => {
  const d = parseLocalDate(s);
  return d ? d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
};

const AdminAgeMismatchPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [rows, setRows] = useState([]);
  const [calculatedAsOf, setCalculatedAsOf] = useState(null);
  const [seasonYear, setSeasonYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [includeLocked, setIncludeLocked] = useState(false);
  const [includeOverrides, setIncludeOverrides] = useState(false);
  const [includeUnrostered, setIncludeUnrostered] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [actingPlayerId, setActingPlayerId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [playUpModal, setPlayUpModal] = useState(null); // { player, candidates, target_division_id, reason_if_empty } | null
  const [playUpFetching, setPlayUpFetching] = useState(false);
  const [playUpChoiceTeamId, setPlayUpChoiceTeamId] = useState('');
  const [playUpReason, setPlayUpReason] = useState('');
  const [playUpSubmitting, setPlayUpSubmitting] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams();
      if (includeLocked) qs.set('include_locked', 'true');
      if (includeOverrides) qs.set('include_overrides', 'true');
      if (includeUnrostered) qs.set('include_unrostered', 'true');
      const url = `${API_URL}/api/admin/roster/age-mismatch-report${qs.toString() ? `?${qs}` : ''}`;
      const r = await axios.get(url, { withCredentials: true });
      setRows(r.data?.players || []);
      setCalculatedAsOf(r.data?.calculated_as_of || null);
      setSeasonYear(r.data?.season_year || null);
    } catch (e) {
      setError(errorToString(e.response?.data?.detail, 'Failed to load age-mismatch report'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) return;
    fetchReport();
     
  }, [includeLocked, includeOverrides, includeUnrostered, isSuperAdmin]);

  // Global player search — hits /api/admin/players/search when the query
  // is long enough. Debounced 250ms to prevent a fetch per keystroke.
  useEffect(() => {
    if (!isSuperAdmin) return;
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(
          `${API_URL}/api/admin/players/search`,
          { params: { q }, withCredentials: true }
        );
        if (!cancelled) setSearchResults(r.data?.players || []);
      } catch (e) {
        if (!cancelled) {
          setSearchResults([]);
          // Only surface hard errors (403/500); ignore 400 (min-length is
          // gated client-side too).
          const s = e.response?.status;
          if (s && s !== 400) {
            setError(errorToString(e.response?.data?.detail, 'Player search failed'));
          }
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [searchQuery, isSuperAdmin]);

  const handleLock = async (player) => {
    const ok = window.confirm(
      `Lock ${player.first_name} ${player.last_name}'s team assignment?\n\n` +
      'Locked players are excluded from the age-mismatch recommender. ' +
      'This does NOT block conscious super-admin moves via the roster page.'
    );
    if (!ok) return;
    const reasonRaw = window.prompt(
      'Reason (shown to other admins, optional, max 300 chars):',
      ''
    );
    if (reasonRaw === null) return; // user cancelled the prompt
    const reason = (reasonRaw || '').slice(0, 300);

    setActingPlayerId(player.player_id);
    setError('');
    setSuccessMessage('');
    try {
      await axios.post(
        `${API_URL}/api/players/${player.player_id}/team-lock`,
        { reason },
        { withCredentials: true }
      );
      setSuccessMessage(`Locked ${player.first_name} ${player.last_name}.`);
      await fetchReport();
    } catch (e) {
      setError(errorToString(e.response?.data?.detail, 'Failed to lock player'));
    } finally {
      setActingPlayerId(null);
    }
  };

  const handleUnlock = async (player) => {
    const ok = window.confirm(
      `Unlock ${player.first_name} ${player.last_name}?\n\n` +
      'They will reappear in the default age-mismatch report.'
    );
    if (!ok) return;
    setActingPlayerId(player.player_id);
    setError('');
    setSuccessMessage('');
    try {
      await axios.delete(
        `${API_URL}/api/players/${player.player_id}/team-lock`,
        { withCredentials: true }
      );
      setSuccessMessage(`Unlocked ${player.first_name} ${player.last_name}.`);
      await fetchReport();
    } catch (e) {
      setError(errorToString(e.response?.data?.detail, 'Failed to unlock player'));
    } finally {
      setActingPlayerId(null);
    }
  };

  const openPlayUpModal = async (player) => {
    setPlayUpModal({ player, candidates: [], target_division_id: null, reason_if_empty: null });
    setPlayUpChoiceTeamId('');
    setPlayUpReason('');
    setPlayUpFetching(true);
    setError('');
    try {
      const r = await axios.get(
        `${API_URL}/api/admin/players/${player.player_id}/play-up-candidates`,
        { withCredentials: true }
      );
      setPlayUpModal({
        player,
        candidates: r.data?.candidates || [],
        target_division_id: r.data?.target_division_id,
        reason_if_empty: r.data?.reason_if_empty || null,
      });
    } catch (e) {
      setError(errorToString(e.response?.data?.detail, 'Failed to load play-up candidates'));
      setPlayUpModal(null);
    } finally {
      setPlayUpFetching(false);
    }
  };

  const submitPlayUp = async () => {
    if (!playUpModal?.player || !playUpChoiceTeamId) return;
    const reason = (playUpReason || '').trim().slice(0, 200);
    if (!reason) {
      setError('Please supply a reason for the play-up override.');
      return;
    }
    setPlayUpSubmitting(true);
    setError('');
    setSuccessMessage('');
    try {
      // Backend /api/roster/move declares player_id + new_team_id as query
      // params, but reads play_up_one_level / play_up_reason from the JSON
      // body. Send both accordingly.
      await axios.post(
        `${API_URL}/api/roster/move`,
        { play_up_one_level: true, play_up_reason: reason },
        {
          params: {
            player_id: playUpModal.player.player_id,
            new_team_id: playUpChoiceTeamId,
          },
          withCredentials: true,
        }
      );
      setSuccessMessage(
        `Moved ${playUpModal.player.first_name} ${playUpModal.player.last_name} up one level.`
      );
      setPlayUpModal(null);
      await fetchReport();
    } catch (e) {
      setError(errorToString(e.response?.data?.detail, 'Play-up move failed'));
    } finally {
      setPlayUpSubmitting(false);
    }
  };

  // Merged view: mismatch report rows PLUS global-search results when the
  // query is active. Rows from the mismatch report keep `is_mismatch:true`;
  // search-only rows carry the flag from the server (`is_mismatch:false`
  // when the player is age-correct). Deduped by player_id — mismatch rows
  // win when both exist (they carry the fuller badge/status data).
  const q = searchQuery.trim().toLowerCase();
  let filteredRows;
  if (!q) {
    filteredRows = rows;
  } else if (q.length < 2) {
    // Local-only filter for the 1-character case so typing feels responsive.
    filteredRows = rows.filter((p) => {
      const full = `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase();
      return full.includes(q);
    });
  } else {
    // 2+ chars: merge server search with any mismatch rows that also match.
    const mismatchMatches = rows
      .filter((p) => `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase().includes(q))
      .map((p) => ({ ...p, is_mismatch: true }));
    const seen = new Set(mismatchMatches.map((p) => p.player_id));
    const extra = (searchResults || []).filter((p) => !seen.has(p.player_id));
    filteredRows = [...mismatchMatches, ...extra];
  }

  // Defense in depth — backend already enforces super_admin, but if a
  // non-super_admin somehow lands on the route show an explicit refusal
  // rather than a misleading empty state.
  if (!isSuperAdmin) {
    return (
      <ParentLayout>
        <div className="max-w-3xl mx-auto px-4 py-10 text-center" data-testid="age-mismatch-unauthorized">
          <ShieldAlert className="w-12 h-12 mx-auto text-red mb-3" />
          <h1 className="font-display text-xl font-black text-navy uppercase mb-2">Super-admin only</h1>
          <p className="text-sm text-gray-600">The age-mismatch report is restricted to super-admin accounts.</p>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-4" data-testid="admin-age-mismatch-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} data-testid="age-mismatch-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="font-display text-2xl font-black text-navy uppercase tracking-tight flex items-center">
                <ShieldAlert className="w-6 h-6 mr-2 text-gold" /> Age Mismatch
              </h1>
              <p className="text-sm text-gray-600">
                Rostered players whose stored age group no longer matches their date-of-birth-derived age.
                Review &amp; action only — no automatic team movement.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchReport}
            disabled={loading}
            data-testid="age-mismatch-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <div className="flex flex-wrap gap-3 items-center bg-gray-50 border border-gray-200 rounded-lg p-3">
          <label className="flex items-center space-x-2 text-sm cursor-pointer" data-testid="toggle-locked-label">
            <input
              type="checkbox"
              checked={includeLocked}
              onChange={(e) => setIncludeLocked(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-navy focus:ring-navy"
              data-testid="toggle-include-locked"
            />
            <span>Show locked players</span>
          </label>
          <label className="flex items-center space-x-2 text-sm cursor-pointer" data-testid="toggle-overrides-label">
            <input
              type="checkbox"
              checked={includeOverrides}
              onChange={(e) => setIncludeOverrides(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-navy focus:ring-navy"
              data-testid="toggle-include-overrides"
            />
            <span>Show play-up override players</span>
          </label>
          <label className="flex items-center space-x-2 text-sm cursor-pointer" data-testid="toggle-unrostered-label">
            <input
              type="checkbox"
              checked={includeUnrostered}
              onChange={(e) => setIncludeUnrostered(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-navy focus:ring-navy"
              data-testid="toggle-include-unrostered"
            />
            <span>Show unrostered players</span>
          </label>
          <span className="text-xs text-gray-500 ml-auto" data-testid="age-mismatch-count">
            {loading
              ? 'Loading…'
              : searchQuery.trim()
                  ? (searching
                      ? 'Searching…'
                      : `${filteredRows.length} result${filteredRows.length === 1 ? '' : 's'} for "${searchQuery.trim()}"`)
                  : `${rows.length} ${rows.length === 1 ? 'player' : 'players'}`}
          </span>
        </div>

        <div className="relative" data-testid="age-mismatch-search-wrapper">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search player by name…"
            className="w-full border border-gray-300 rounded-lg pl-9 pr-9 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
            data-testid="age-mismatch-search-input"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 p-1"
              data-testid="age-mismatch-search-clear"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {calculatedAsOf && (
          <div className="flex items-center text-xs text-gray-600 -mt-1" data-testid="age-mismatch-cutoff-note">
            <CalendarClock className="w-3.5 h-3.5 mr-1.5 text-gold" />
            Ages computed as of {new Date(calculatedAsOf + 'T00:00:00').toLocaleDateString('en-US', {
              year: 'numeric', month: 'long', day: 'numeric'
            })}
            {seasonYear ? ` (${seasonYear} season cutoff)` : ''}.
          </div>
        )}

        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center" data-testid="age-mismatch-success">
            <CheckCircle2 className="w-4 h-4 text-green-700 mr-2" />
            <p className="text-sm text-green-800">{successMessage}</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center" data-testid="age-mismatch-error">
            <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="space-y-2" data-testid="age-mismatch-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-20 bg-gray-100 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : searchQuery.trim() ? (
          // Search-active branch: don't show the "no mismatches" copy just
          // because the mismatch report is empty — the user is looking for
          // any player, not just mismatches.
          filteredRows.length === 0 && !searching ? (
            <Card data-testid="age-mismatch-search-empty">
              <CardContent className="py-10 text-center">
                <Search className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-700 font-semibold">
                  {searchQuery.trim().length < 2
                    ? 'Type at least 2 characters to search all players.'
                    : `No players matched "${searchQuery}".`}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {searchQuery.trim().length < 2
                    ? 'The mismatch report is still visible when the search is cleared.'
                    : 'Try a different name or clear the search.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredRows.map((p) => (
            <Card key={p.player_id} data-testid={`age-mismatch-row-${p.player_id}`}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => navigate(`/admin/players/${p.player_id}/report`)}
                        className="font-semibold text-navy hover:underline text-base"
                        data-testid={`age-mismatch-name-${p.player_id}`}
                      >
                        {p.first_name} {p.last_name}
                      </button>
                      {p.is_mismatch === false ? (
                        <Badge className="bg-gray-100 text-gray-700 border-gray-300" data-testid={`badge-search-match-${p.player_id}`}>
                          <Search className="w-3 h-3 mr-1" /> Search match
                        </Badge>
                      ) : (
                        <Badge className="bg-orange-100 text-orange-800 border-orange-300" data-testid={`badge-mismatch-${p.player_id}`}>
                          <AlertCircle className="w-3 h-3 mr-1" /> Age mismatch
                        </Badge>
                      )}
                      {p.team_locked && (
                        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300" data-testid={`badge-locked-${p.player_id}`}>
                          <Lock className="w-3 h-3 mr-1" /> Locked
                        </Badge>
                      )}
                      {p.age_group_override && (
                        <Badge className="bg-blue-100 text-blue-800 border-blue-300" data-testid={`badge-override-${p.player_id}`}>
                          <ArrowUpRight className="w-3 h-3 mr-1" /> Plays up
                        </Badge>
                      )}
                    </div>

                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs text-gray-600">
                      <div>
                        <span className="text-gray-400 uppercase tracking-wider mr-1">DOB</span>
                        <span className="text-gray-800">{fmtDOB(p.date_of_birth)}</span>
                      </div>
                      <div data-testid={`age-mismatch-shift-${p.player_id}`}>
                        <span className="text-gray-400 uppercase tracking-wider mr-1">Age group</span>
                        <span className="text-gray-800">
                          {p.is_mismatch === false ? (
                            <>{p.stored_age_group || '—'}{p.season_age_group ? <span className="text-gray-400 ml-1">({p.season_age_group} season)</span> : null}</>
                          ) : (
                            <>{p.stored_age_group || '—'} <span className="text-red font-bold">→</span> {p.calculated_age_group || '—'}</>
                          )}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-400 uppercase tracking-wider mr-1">Team</span>
                        <span className="text-gray-800">{p.current_team_name || (p.current_team_id ? p.current_team_id : <em className="text-gray-500">— unassigned —</em>)}</span>
                      </div>
                      <div>
                        <span className="text-gray-400 uppercase tracking-wider mr-1">Division</span>
                        <span className="text-gray-800">{p.current_division_id || '—'}</span>
                      </div>
                    </div>

                    {p.team_locked && p.team_locked_reason && (
                      <p className="mt-2 text-xs text-gray-500 italic">
                        Lock reason: {p.team_locked_reason}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    {!p.current_team_id ? (
                      <Button
                        size="sm"
                        className="bg-navy hover:bg-navy/90 text-white"
                        onClick={() => navigate(`/admin/players/${p.player_id}/report`)}
                        data-testid={`age-mismatch-assign-${p.player_id}`}
                      >
                        <ArrowUpRight className="w-4 h-4 mr-1" />
                        Assign
                      </Button>
                    ) : p.team_locked ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUnlock(p)}
                          disabled={actingPlayerId === p.player_id}
                          data-testid={`age-mismatch-unlock-${p.player_id}`}
                        >
                          <Unlock className="w-4 h-4 mr-1" />
                          {actingPlayerId === p.player_id ? 'Unlocking…' : 'Unlock'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/players/${p.player_id}/report`)}
                          className="text-navy hover:bg-navy/5"
                          data-testid={`age-mismatch-reassign-${p.player_id}`}
                        >
                          <ArrowUpRight className="w-4 h-4 mr-1" />
                          Reassign
                        </Button>
                      </>
                    ) : p.age_group_override ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/players/${p.player_id}/report`)}
                        className="text-navy hover:bg-navy/5"
                        data-testid={`age-mismatch-reassign-${p.player_id}`}
                      >
                        <ArrowUpRight className="w-4 h-4 mr-1" />
                        Reassign
                      </Button>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-blue-400 text-blue-700 hover:bg-blue-50"
                          onClick={() => openPlayUpModal(p)}
                          disabled={actingPlayerId === p.player_id}
                          data-testid={`age-mismatch-playup-${p.player_id}`}
                        >
                          <ArrowUpRight className="w-4 h-4 mr-1" />
                          Play up
                        </Button>
                        <Button
                          size="sm"
                          className="bg-red hover:bg-red/90 text-white"
                          onClick={() => handleLock(p)}
                          disabled={actingPlayerId === p.player_id}
                          data-testid={`age-mismatch-lock-${p.player_id}`}
                        >
                          <Lock className="w-4 h-4 mr-1" />
                          {actingPlayerId === p.player_id ? 'Locking…' : 'Lock'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/players/${p.player_id}/report`)}
                          className="text-navy hover:bg-navy/5"
                          data-testid={`age-mismatch-reassign-${p.player_id}`}
                        >
                          <ArrowUpRight className="w-4 h-4 mr-1" />
                          Reassign
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
          )
        ) : rows.length === 0 ? (
          <Card data-testid="age-mismatch-empty">
            <CardContent className="py-10 text-center">
              <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-3" />
              <p className="text-sm text-gray-700 font-semibold">No age-group mismatches detected.</p>
              <p className="text-xs text-gray-500 mt-1">
                Players already locked or with play-up overrides are excluded — toggle the filters above to include them.
                You can also search for any player by name above.
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredRows.map((p) => (
            <Card key={p.player_id} data-testid={`age-mismatch-row-${p.player_id}`}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => navigate(`/admin/players/${p.player_id}/report`)}
                        className="font-semibold text-navy hover:underline text-base"
                        data-testid={`age-mismatch-name-${p.player_id}`}
                      >
                        {p.first_name} {p.last_name}
                      </button>
                      {p.team_locked && (
                        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300" data-testid={`badge-locked-${p.player_id}`}>
                          <Lock className="w-3 h-3 mr-1" /> Locked
                        </Badge>
                      )}
                      {p.age_group_override && (
                        <Badge className="bg-blue-100 text-blue-800 border-blue-300" data-testid={`badge-override-${p.player_id}`}>
                          <ArrowUpRight className="w-3 h-3 mr-1" /> Plays up
                        </Badge>
                      )}
                    </div>

                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs text-gray-600">
                      <div>
                        <span className="text-gray-400 uppercase tracking-wider mr-1">DOB</span>
                        <span className="text-gray-800">{fmtDOB(p.date_of_birth)}</span>
                      </div>
                      <div data-testid={`age-mismatch-shift-${p.player_id}`}>
                        <span className="text-gray-400 uppercase tracking-wider mr-1">Age group</span>
                        <span className="text-gray-800">
                          {p.stored_age_group || '—'} <span className="text-red font-bold">→</span> {p.calculated_age_group || '—'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-400 uppercase tracking-wider mr-1">Team</span>
                        <span className="text-gray-800">{p.current_team_name || (p.current_team_id ? p.current_team_id : <em className="text-gray-500">— unassigned —</em>)}</span>
                      </div>
                      <div>
                        <span className="text-gray-400 uppercase tracking-wider mr-1">Division</span>
                        <span className="text-gray-800">{p.current_division_id || '—'}</span>
                      </div>
                    </div>

                    {p.team_locked && p.team_locked_reason && (
                      <p className="mt-2 text-xs text-gray-500 italic">
                        Lock reason: {p.team_locked_reason}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    {!p.current_team_id ? (
                      <Button
                        size="sm"
                        className="bg-navy hover:bg-navy/90 text-white"
                        onClick={() => navigate(`/admin/players/${p.player_id}/report`)}
                        data-testid={`age-mismatch-assign-${p.player_id}`}
                      >
                        <ArrowUpRight className="w-4 h-4 mr-1" />
                        Assign
                      </Button>
                    ) : p.team_locked ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUnlock(p)}
                          disabled={actingPlayerId === p.player_id}
                          data-testid={`age-mismatch-unlock-${p.player_id}`}
                        >
                          <Unlock className="w-4 h-4 mr-1" />
                          {actingPlayerId === p.player_id ? 'Unlocking…' : 'Unlock'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/players/${p.player_id}/report`)}
                          className="text-navy hover:bg-navy/5"
                          data-testid={`age-mismatch-reassign-${p.player_id}`}
                        >
                          <ArrowUpRight className="w-4 h-4 mr-1" />
                          Reassign
                        </Button>
                      </>
                    ) : p.age_group_override ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/players/${p.player_id}/report`)}
                        className="text-navy hover:bg-navy/5"
                        data-testid={`age-mismatch-reassign-${p.player_id}`}
                      >
                        <ArrowUpRight className="w-4 h-4 mr-1" />
                        Reassign
                      </Button>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-blue-400 text-blue-700 hover:bg-blue-50"
                          onClick={() => openPlayUpModal(p)}
                          disabled={actingPlayerId === p.player_id}
                          data-testid={`age-mismatch-playup-${p.player_id}`}
                        >
                          <ArrowUpRight className="w-4 h-4 mr-1" />
                          Play up
                        </Button>
                        <Button
                          size="sm"
                          className="bg-red hover:bg-red/90 text-white"
                          onClick={() => handleLock(p)}
                          disabled={actingPlayerId === p.player_id}
                          data-testid={`age-mismatch-lock-${p.player_id}`}
                        >
                          <Lock className="w-4 h-4 mr-1" />
                          {actingPlayerId === p.player_id ? 'Locking…' : 'Lock'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/players/${p.player_id}/report`)}
                          className="text-navy hover:bg-navy/5"
                          data-testid={`age-mismatch-reassign-${p.player_id}`}
                        >
                          <ArrowUpRight className="w-4 h-4 mr-1" />
                          Reassign
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}

        {playUpModal && (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            data-testid="playup-modal"
          >
            <div className="bg-white rounded-xl max-w-lg w-full border border-gray-200 max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-navy border-b border-navy p-4 flex items-center justify-between rounded-t-xl">
                <div className="flex items-center space-x-2">
                  <ArrowUpRight className="w-5 h-5 text-gold" />
                  <h2 className="font-display text-lg font-black text-white uppercase tracking-tight">
                    Play up one level
                  </h2>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPlayUpModal(null)}
                  className="text-white hover:bg-white/10"
                  data-testid="playup-close"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="p-4 space-y-4">
                <p className="text-sm text-gray-700">
                  Move <strong>{playUpModal.player.first_name} {playUpModal.player.last_name}</strong>{' '}
                  from <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">
                    {playUpModal.player.current_division_id || '—'}
                  </code>{' '}
                  up one division. This records a super-admin override on the roster assignment.
                </p>

                {playUpFetching ? (
                  <div className="h-16 bg-gray-100 animate-pulse rounded" data-testid="playup-loading" />
                ) : playUpModal.candidates.length === 0 ? (
                  <div
                    className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-900"
                    data-testid="playup-empty"
                  >
                    <AlertCircle className="w-4 h-4 inline mr-1 text-yellow-700" />
                    {playUpModal.reason_if_empty || 'No eligible destination team.'}
                  </div>
                ) : (
                  <>
                    <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">
                      Destination team
                    </label>
                    <select
                      value={playUpChoiceTeamId}
                      onChange={(e) => setPlayUpChoiceTeamId(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                      data-testid="playup-team-select"
                    >
                      <option value="">— Pick a team —</option>
                      {playUpModal.candidates.map((c) => (
                        <option key={c.team_id} value={c.team_id}>
                          {c.team_name} ({c.division_id})
                        </option>
                      ))}
                    </select>

                    <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1 mt-2">
                      Reason (required, max 200 chars)
                    </label>
                    <textarea
                      value={playUpReason}
                      onChange={(e) => setPlayUpReason(e.target.value.slice(0, 200))}
                      rows={3}
                      placeholder="e.g. Advanced athlete, coach and family concur"
                      className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                      data-testid="playup-reason"
                    />
                  </>
                )}
              </div>

              <div className="px-4 py-3 border-t border-gray-200 flex justify-end space-x-2 bg-gray-50 rounded-b-xl">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPlayUpModal(null)}
                  disabled={playUpSubmitting}
                  data-testid="playup-cancel"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="bg-blue-700 hover:bg-blue-800 text-white"
                  disabled={
                    playUpSubmitting ||
                    playUpFetching ||
                    playUpModal.candidates.length === 0 ||
                    !playUpChoiceTeamId ||
                    !(playUpReason || '').trim()
                  }
                  onClick={submitPlayUp}
                  data-testid="playup-submit"
                >
                  <ArrowUpRight className="w-4 h-4 mr-1" />
                  {playUpSubmitting ? 'Moving…' : 'Play up'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ParentLayout>
  );
};

export default AdminAgeMismatchPage;
